import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { toast } from '@cotera/client/app/components/ui';
import { err, ok } from 'neverthrow';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { CocoChatModel } from '../models/coco-chat.model';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

/**
 * ExecuteToolAction for coco-chat flow.
 * Handles tool execution with approval/denial support via ChatActions.
 * This is a separate action from the v0 ExecuteToolAction to allow for different behavior.
 */
export class ExecuteToolAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  static readonly actionId = 'ExecuteToolAction';
  title = 'Execute Tool';
  inputSchema = z.object({
    toolCallId: z.string(),
    approve: z.boolean().optional(),
    deny: z.boolean().optional(),
    reason: z.string().optional(),
    conversationId: z.string().optional(),
  });
  group = 'Chat';

  constructor(
    private readonly client: Pick<TenantedClient, 'tools' | 'resource'>,
    private readonly getBearerToken: () => Promise<string | undefined>,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: {
      toolCallId: string;
      approve?: boolean;
      deny?: boolean;
      reason?: string;
      conversationId?: string;
    },
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel)!;

    // Use provided conversationId for sub-agent tools, otherwise use the main chat's
    const targetConversationId = params.conversationId ?? model.id;

    // Determine action: explicitly check both approve and deny parameters
    let action: 'approve' | 'deny';
    if (params.approve === true) {
      action = 'approve';
    } else if (params.deny === true) {
      action = 'deny';
    } else {
      // Neither approve nor deny was explicitly set to true
      return err([
        {
          t: 'error',
          message: 'Either approve or deny must be set to true',
        },
      ]);
    }

    // For sub-agent tools (different conversationId), skip local lookup
    // The backend will find the tool call in the correct conversation
    const isSubAgentTool =
      params.conversationId !== undefined && params.conversationId !== model.id;

    if (!isSubAgentTool) {
      // Only validate tool exists for main chat tools
      const conversation = model.conversationWatchable?.snapshot?.();
      if (!conversation) {
        return err([{ t: 'error', message: 'Conversation not found' }]);
      }

      const toolCall = conversation.findToolCall(params.toolCallId);
      if (!toolCall) {
        toast.error(`Tool call ${params.toolCallId} not found`);
        return err([{ t: 'error', message: 'Tool call not found' }]);
      }
    }

    model.statusWatchable.set('submitted');

    const result = await this.client.resource.chat.runTool({
      model: model.model,
      conversationId: targetConversationId,
      toolCallId: params.toolCallId,
      action,
      reason: params.reason,
    });

    if (result.isErr()) {
      model.statusWatchable.set('error');
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to execute tool';
      toast.error(errorMessage);
      return err([{ t: 'error', message: errorMessage }]);
    }

    void model.chatStatusWatchable.refresh();

    // Fallback: if status stays 'submitted', force to ready (see coco-submit-chat.action)
    setTimeout(() => {
      if (model.statusWatchable.snapshot() === 'submitted') {
        model.statusWatchable.set('ready');
      }
    }, 2000);

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    {
      toolCallId: string;
      approve?: boolean;
      deny?: boolean;
      reason?: string;
      conversationId?: string;
    },
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    const action =
      payload.approve === true
        ? 'approve'
        : payload.deny === true
        ? 'deny'
        : null;
    if (action === null) {
      return null;
    }

    // Use provided conversationId for sub-agent tools
    const targetConversationId = payload.conversationId ?? model.conversationId;

    return cocoActionMetadata(CocoEvent.ToolExecuted, {
      conversationId: targetConversationId,
      surface: getChatSurface(context),
      toolCallId: payload.toolCallId,
      action,
      hasReason: (payload.reason ?? '').trim().length > 0,
    });
  }
}
